import { classNames } from "@/utils/classnames";
import React, { useCallback, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";

// High-DPI screens scroll in fractional pixels, so the true end can leave a
// sub-pixel remainder between scrollTop + clientHeight and scrollHeight.
const SCROLL_END_TOLERANCE_PX = 1;

export type MenuScrollEndFadeHandle = {
  /** Updates the fade and returns the remaining scroll distance. */
  update: (scroller: HTMLElement) => number;
};

/**
 * Fades out the bottom edge of the menu scroller it is rendered into while more
 * options sit below it, and clears once the list is scrolled to the end.
 *
 * Render it as the scroller's last child: `sticky` pins it to the scroller's
 * bottom edge, and its negative margin cancels its own height so it never
 * lengthens the list. The gradient ends in the menu surface colour, so it only
 * belongs on MenuContainerV2 surfaces, and it stops short of opaque so the row
 * under it stays legible.
 */
export const MenuScrollEndFade = React.forwardRef<MenuScrollEndFadeHandle>(
  function MenuScrollEndFade(_props, ref) {
    const fadeRef = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(false);

    const update = useCallback((scroller: HTMLElement) => {
      const remaining = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      setVisible(remaining > SCROLL_END_TOLERANCE_PX);
      return remaining;
    }, []);

    useImperativeHandle(ref, () => ({ update }), [update]);

    useLayoutEffect(() => {
      const scroller = fadeRef.current?.parentElement;
      if (scroller == null) {
        return;
      }

      const updateCurrentScroller = () => update(scroller);

      // The list can lengthen without the scroller resizing (filtering, or a
      // virtualizer measuring rows), so watch its children too, and re-observe
      // when sections are added or removed.
      const resizeObserver =
        typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateCurrentScroller);
      const observeChildren = () => {
        resizeObserver?.disconnect();
        resizeObserver?.observe(scroller);
        if (resizeObserver != null) {
          for (const child of Array.from(scroller.children)) {
            resizeObserver.observe(child);
          }
        }
      };
      const mutationObserver =
        typeof MutationObserver === "undefined"
          ? null
          : new MutationObserver(() => {
              observeChildren();
              updateCurrentScroller();
            });

      observeChildren();
      mutationObserver?.observe(scroller, { childList: true });
      updateCurrentScroller();

      return () => {
        mutationObserver?.disconnect();
        resizeObserver?.disconnect();
      };
    }, [update]);

    return (
      <div
        ref={fadeRef}
        aria-hidden={true}
        className={classNames(
          "pointer-events-none sticky bottom-0 -mt-6 h-6",
          "bg-gradient-to-b from-transparent to-cds-surface-default/70 dark:to-cds-elevated-dark/70",
          "transition-opacity duration-150",
          visible ? "opacity-100" : "opacity-0",
        )}
      />
    );
  },
);
